import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useSubscription } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import Token from '../Token';
import { FormControl, Grid, InputLabel, Paper, Select, Typography, Zoom } from '@material-ui/core';
import { ArrowForwardRounded, FilterListRounded } from '@material-ui/icons';
import { utils } from 'ethers';
import moment from 'moment';

const QUERY_FOR_RESERVE_LIST = gql`
  {
    reserves(where: { isTradeEnabled: true, isRemoved: false }) {
      id
      trades(first:1, skip:0) {
        id
      }
    }
  }  
`;

const SUBSCRIPTION_FOR_PROXY_TRADES = gql`
    subscription {
        proxyTrades(first: 20, skip: 0, orderBy: createdAtBlockTimestamp, orderDirection: desc) {
            id
            trader {
                id
            }
            src
            dest
            actualSrcAmount
            actualDestAmount
            createdAtBlockTimestamp
            createdAtBlockNumber
            createdAtLogIndex
        }
    }
`;

const SUBSCRIPTION_FOR_RESERVE_TRADES = (reserveId) => gql`
    subscription {
        reserveTrades(
            where:{reserve: "${reserveId}" },
            first: 20, skip: 0, 
            orderBy: createdAtBlockTimestamp, 
            orderDirection: desc) {
            id
            src
            dest
            actualSrcAmount: srcAmount
            actualDestAmount: destAmount
            createdAtBlockTimestamp
        }
    }
`;

function useInterval(callback, delay) {
    const savedCallback = useRef();
  
    // Remember the latest callback.
    useEffect(() => {
      savedCallback.current = callback;
    }, [callback]);
  
    // Set up the interval.
    useEffect(() => {
      function tick() {
        savedCallback.current();
      }
      if (delay !== null) {
        let id = setInterval(tick, delay);
        return () => clearInterval(id);
      }
    }, [delay]);
  }

function sliceAddress(address) {
    return `${address.slice(0,6)}...${address.slice(-4)}`
}

function convertWei(tokenAddress, amount) {
    const decimals = 18
    const wei = utils.bigNumberify(amount)
    // TODO - get actual decimals for tokenAddress
    return parseFloat(utils.formatUnits(wei, decimals)).toLocaleString([], ({minimumFractionDigits: 4, maximumFractionDigits: 4}))
}

const MomentTimeAgo = ({ id, timestamp }) => {
    const momentObject = moment(parseInt(timestamp) * 1000)
    const [fromNow, setFromNow] = useState(momentObject.fromNow())

    useInterval(() => {
        setFromNow(momentObject.fromNow())
    }, 1000)

    return fromNow
}

const RecentTradesHeader = ({ loading }) => {
    return (
        <React.Fragment>
            <Typography variant="h6" style={{ marginTop: 18 }} align='left'>Live feed</Typography>
            <Typography variant="body2" align='left' gutterBottom>{loading ? "Connecting..." : "• Connected"}</Typography>
        </React.Fragment>
    )
}

const RecentTradesContent = ({ data }) => {

    if (!data) return null
  
    return data.map(({ id, trader, src, dest, actualSrcAmount, actualDestAmount, createdAtBlockTimestamp }) => (
        <Zoom in={true} key={id}>
        <Paper elevation={2} style={{ marginBottom: 18, padding: 8 }}>
            <Grid container direction="column" justify="flex-start" alignItems="stretch">
                <Grid item>
                    <Typography variant="caption">
                        <MomentTimeAgo id={id} timestamp={createdAtBlockTimestamp} />
                    </Typography>
                </Grid>
                <Grid item>
                    <Grid container direction="row" justify="center" alignItems="center" spacing={2}>
                        <Grid item>
                            <Grid container direction="column" alignItems="center">
                                <Grid item>
                                    <Token address={src} />
                                </Grid>
                                <Grid item>
                                    <Typography>
                                        {`${convertWei(src, actualSrcAmount)}`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <ArrowForwardRounded style={{ fontSize: 48 }} />
                        </Grid>
                        <Grid item>
                            <Grid container direction="column" alignItems="center">
                                <Grid item>
                                    <Token address={dest} />
                                </Grid>
                                <Grid item>
                                    <Typography>
                                        {`${convertWei(dest, actualDestAmount)}`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    <Typography variant="caption">
                        {trader ? sliceAddress(trader.id) : null}
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
        </Zoom>
    ));
  }

  const Filter = ({ isDisabled, value, setValue }) => {
    /* 
    //  HTTP and WebSocket hooks
    //    - for http: useQuery
    //    - for ws: useSubscription
    */

    const { loading, error, data } = useQuery(QUERY_FOR_RESERVE_LIST);

    if (error) console.log(error)

    return (
        <Grid container direction="row" alignItems="flex-end" spacing={2}>
            <Grid item>
                <FilterListRounded />
            </Grid>
            <Grid item>
                <FormControl>
                    <InputLabel htmlFor="kyber-reserve">Reserve</InputLabel>
                    <Select
                    native
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    inputProps={{
                        name: 'reserve',
                        id: 'kyber-reserve',
                    }}
                    disabled={loading || isDisabled}
                    >
                    <option value={undefined} />
                    { data && data.reserves.map((reserve) => {
                        return <option disabled={reserve.trades.length === 0} value={reserve.id} key={reserve.id}>{ sliceAddress(reserve.id) }</option>
                    })}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    )
  }

  const RecentTrades = () => {
    const [reserveFilter, setReserveFilter] = useState(undefined)

    /* 
    //  HTTP and WebSocket hooks
    //    - for http: useQuery
    //    - for ws: useSubscription
    */

    const subscriptionQuery = reserveFilter ? SUBSCRIPTION_FOR_RESERVE_TRADES(reserveFilter) : SUBSCRIPTION_FOR_PROXY_TRADES
    const dataKey = reserveFilter ? 'reserveTrades' : 'proxyTrades'

    const { loading, error, data } = useSubscription(subscriptionQuery)

    if (error) console.log(error)

    return (
        <Grid container direction="column" spacing={2}>
            <Grid item>
                <Grid container direction="row" alignItems="flex-end" justify="space-between">
                    <Grid item>
                        <RecentTradesHeader loading={loading} />
                    </Grid>
                    <Grid item>
                        <Filter value={reserveFilter} setValue={setReserveFilter} isDisabled={loading} />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <RecentTradesContent data={data && data[dataKey]} />
            </Grid>
        </Grid>
    )
  }

export default RecentTrades